import styled from '@emotion/styled'
import { WebLink } from '@news-mono/web-common'
import {
    LabelledToggle,
    LabelledToggleProps,
} from '../../buttons/LabelledToggle/LabelledToggle'
import { PerthNowLogoHorizontal } from '../../logos/PerthNowLogo/PerthNowLogo'
import { TheWestLogo } from '../../logos/TheWestLogo/TheWestLogo'
import { calcRem, colors, fonts, themedValue } from '../../__styling'
import { breakpoint } from '../../__styling/style-functions'
import { palette } from '../EmailPreference.styled'

export const StyledMastheadPreference = styled('div')(() => ({
    marginBottom: calcRem(80),
    '& > div:first-child::after': {
        display: 'none',
    },
}))

export const StyledSubscriberExclusiveMessage = styled('div')(({ theme }) => ({
    color: palette(theme)?.text.secondary,
    lineHeight: calcRem(24),
    fontWeight: 400,
    maxWidth: calcRem(520),
    marginBottom: calcRem(16),
    p: {
        margin: 0,
    },
}))

export const StyledSubscriptionButton = styled(WebLink)(({ theme }) => ({
    fontFamily: theme.fonts.sansSerif,
    fontSize: calcRem(13),
    fontWeight: 700,
    background: theme.colors.brand,
    color: colors.white,
    padding: calcRem(7, 0),
    borderRadius: '2px',
    textDecoration: 'none',
    display: 'block',
    marginBottom: calcRem(16),
    width: '100%',
    maxWidth: calcRem(520),
    textAlign: 'center',

    [breakpoint('sm')]: {
        maxWidth: `calc(50% - ${calcRem(16)})`,
    },
    [breakpoint('md')]: {
        maxWidth: `calc((100% - ${calcRem(32 * 2)})/3)`,
    },
    [breakpoint('lg')]: {
        maxWidth: `calc((100% - ${calcRem(40 * 2)})/3)`,
    },
}))
export const StyledSectionHeader = styled('h2')(({ theme }) => ({
    fontSize: themedValue(theme, {
        thewest: calcRem(32),
        perthnow: calcRem(36),
        fallback: calcRem(32),
    }),
    fontWeight: themedValue(theme, {
        thewest: 400,
        perthnow: 700,
        fallback: 400,
    }),
    fontFamily: themedValue(theme, {
        thewest: fonts.thewest.serif,
        perthnow: fonts.perthnow.sansSerif,
        sevennews: fonts.sevennews.sansSerif,
        fallback: undefined,
    }),
    lineHeight: calcRem(40),
    textTransform: 'capitalize',
    margin: calcRem(28, 0, 16),
    '&::after': {
        content: '""',
        display: 'block',
        marginTop: calcRem(8),
        height: calcRem(4),
        width: calcRem(40),
        backgroundColor: theme.colors.brand,
    },
}))

export const StyledPreferenceSection = styled('ul')(({ theme }) => ({
    margin: calcRem(0, 0, 80, 0),
    padding: 0,
    display: 'flex',
    gap: calcRem(16),
    flexDirection: 'column',

    [breakpoint('sm')]: {
        gap: calcRem(32),
        flexDirection: 'row',
        flexWrap: 'wrap',
    },

    [breakpoint('lg')]: themedValue(theme, {
        thewest: {
            gap: calcRem(32, 40),
            maxWidth: calcRem(1220),
        },
        fallback: undefined,
    }),
}))

export const StyledPreferenceCard = styled('li')(({ theme }) => ({
    padding: calcRem(16),
    minWidth: calcRem(270),
    maxWidth: calcRem(520),
    backgroundColor: colors.white,
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between',
    small: {
        fontFamily: themedValue(theme, {
            thewest: fonts.thewest.sansSerif,
            fallback: fonts.thewest.sansSerif,
        }),
        fontWeight: 500,
        fontSize: calcRem(14),
        lineHeight: calcRem(16),
        color: palette(theme)?.text.mute,
        '&:last-child': {
            color: palette(theme)?.text.danger,
            fontWeight: 300,
            position: 'absolute',
            bottom: calcRem(-18),
            left: 0,
            transition: 'all .2s ease-in-out',
        },
    },
    '& > div:first-child': {
        display: 'flex',
        flexDirection: 'column',
        marginRight: calcRem(8),
        h3: {
            margin: calcRem(8, 0),
            fontSize: calcRem(22),
            lineHeight: calcRem(24),
            fontWeight: 700,
            fontFamily: themedValue(theme, {
                thewest: fonts.thewest.serif,
                perthnow: fonts.perthnow.sansSerif,
                sevennews: fonts.sevennews.sansSerif,
                fallback: undefined,
            }),
        },
        small: {
            textTransform: 'uppercase',
        },
        p: {
            fontWeight: 400,
            lineHeight: calcRem(24),
            margin: 0,
        },
    },
    '& > div:last-child': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        minWidth: calcRem(100),
        position: 'relative',

        small: {
            display: 'block',
            textAlign: 'center',
            marginBottom: calcRem(2),
        },
    },
    border: `1px solid ${palette(theme)?.borders.mute}`,
    [breakpoint('sm')]: {
        justifyContent: 'space-between',
        flexBasis: `calc(50% - ${calcRem(16)})`,
        flexGrow: 0,
    },
    [breakpoint('md')]: {
        flexBasis: `calc((100% - ${calcRem(32 * 2)})/3)`,
    },
    [breakpoint('lg')]: themedValue(theme, {
        thewest: {
            flexBasis: `calc((100% - ${calcRem(40 * 2)})/3)`,
        },
        fallback: undefined,
    }),
}))

export const StyledToggleButton = styled(LabelledToggle)<LabelledToggleProps>(
    () => ({
        button: {
            height: calcRem(40),
            padding: calcRem(4),
            background: colors.white,
            width: '100%',
            span: {
                height: '100%',
                borderRadius: 0,
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
            },
        },
    }),
)

export const StyledSubscriptionGroup = styled('ul')(() => ({
    padding: 0,
    margin: 0,
}))

export const StyledTheWestLogo = styled(TheWestLogo)(() => ({
    display: 'block',
    height: calcRem(30),
    width: calcRem(280),

    [breakpoint('sm')]: {
        height: calcRem(35),
        width: calcRem(370),
    },
}))

export const StyledPerthNowLogo = styled(PerthNowLogoHorizontal)(() => ({
    display: 'block',
    height: calcRem(45),
    width: calcRem(230),

    [breakpoint('sm')]: {
        height: calcRem(50),
        width: calcRem(240),
    },
}))

export const StyledExclusiveBanner = styled('div')({
    display: 'flex',
    alignItems: 'center',

    svg: {
        height: calcRem(14),
        width: calcRem(14),
        marginRight: calcRem(3),
    },

    span: {
        color: colors.thewest.westblue,
        fontWeight: 500,
        width: calcRem(120),
        fontSize: calcRem(14),
        lineHeight: calcRem(16),
        fontFamily: fonts.thewest.sansSerif,
    },
})

export const StyledToggleButtonGroup = styled('div')<{ disabled: boolean }>(
    ({ disabled }) => ({
        opacity: disabled ? '0.4' : '1',
    }),
)
